import React, { useState } from "react";
import { CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * Bottom "Aksi Status" panel of the SPB detail page:
 *   • "Tandai Sudah Termuat" with optional jumlah_termuat input
 *   • Status change buttons (belum, mismatch, diverifikasi for petugas)
 *   • Admin-only Ganti Plat button (opens dialog owned by parent)
 *   • Danger zone: Restore, Batalkan, Hapus Permanen (role-gated)
 *
 * Extracted from `SPBDetail.jsx` Feb 2026. Local `jumlahTermuatInput` state
 * stays here because it's only used to feed the confirm button.
 */
export default function SPBActions({
  spb, user,
  onSetStatus, onCancel, onDelete, onRestore, onOpenPlateDialog,
}) {
  const [jumlahTermuatInput, setJumlahTermuatInput] = useState("");

  const handleTermuat = () => {
    const extra = {};
    if (jumlahTermuatInput !== "" && !Number.isNaN(Number(jumlahTermuatInput))) {
      extra.jumlah_termuat = Number(jumlahTermuatInput);
    }
    onSetStatus("sudah_termuat", extra);
  };

  const isLoaded = spb.status === "sudah_termuat" || spb.status === "mismatch";

  return (
    <div className="border border-border p-6 bg-card no-print">
      <div className="overline mb-4">Aksi Status</div>

      {/* Set Sudah Termuat with jumlah input */}
      <div className="border border-border p-4 mb-4 bg-secondary/30">
        <div className="text-sm font-semibold mb-2">Tandai Sudah Termuat</div>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
          <div className="flex-1">
            <label className="overline block mb-1">Jumlah Termuat (opsional)</label>
            <input
              type="number"
              step="0.01"
              value={jumlahTermuatInput}
              onChange={(e) => setJumlahTermuatInput(e.target.value)}
              placeholder={`Rencana: ${spb.jumlah} ${spb.satuan}`}
              className="w-full h-10 px-3 border border-border bg-background rounded-lg text-sm"
              data-testid="input-jumlah-termuat"
            />
          </div>
          <Button
            className="rounded-lg font-bold uppercase tracking-wider"
            onClick={handleTermuat}
            data-testid="set-status-termuat"
          >
            <CheckCircle size={16} className="mr-2" /> Konfirmasi Sudah Termuat
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {spb.status !== "diverifikasi" && (
          <>
            <Button variant="outline" className="rounded-lg"
                    onClick={() => onSetStatus("belum_termuat")}
                    data-testid="set-status-belum">
              Set Belum Termuat
            </Button>
            <Button variant="outline"
                    className="rounded-lg border-destructive text-destructive hover:bg-destructive/5"
                    onClick={() => onSetStatus("mismatch")}
                    data-testid="set-status-mismatch">
              Tandai Mismatch
            </Button>
          </>
        )}
        {user?.role === "petugas" && isLoaded && (
          <Button className="rounded-lg font-bold uppercase tracking-wider"
                  onClick={() => onSetStatus("diverifikasi")}
                  data-testid="set-status-diverifikasi">
            <ShieldCheck size={16} className="mr-2" /> Verifikasi Fisik
          </Button>
        )}
        {user?.role === "admin" && isLoaded && (
          <>
            <div className="inline-flex items-center gap-2 text-xs text-muted-foreground border border-border px-3 py-2 bg-secondary/40"
                 data-testid="admin-verify-hint">
              <ShieldCheck size={14} weight="bold" />
              Verifikasi dilakukan oleh Petugas Loading di lapangan
            </div>
            <Button variant="outline"
                    className="rounded-lg font-bold uppercase tracking-wider border-amber-600 text-amber-700 hover:bg-amber-50"
                    onClick={onOpenPlateDialog}
                    data-testid="admin-override-plate-btn"
                    title="Ganti no polisi setelah SPB termuat (khusus administrator)">
              Ganti Plat
            </Button>
          </>
        )}
      </div>

      {/* Danger zone — admin & admin_kantor, restricted based on loaded state */}
      {(user?.role === "admin" || user?.role === "admin_kantor") && spb.status !== "diverifikasi" && (
        <div className="mt-6 pt-6 border-t border-destructive/30">
          <div className="overline text-destructive mb-3">Zona Berbahaya</div>
          <div className="flex flex-wrap gap-2">
            {spb.status === "dibatalkan" && (
              <Button variant="outline"
                      className="rounded-lg border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                      onClick={onRestore}
                      data-testid="restore-spb-button">
                Restore SPB
              </Button>
            )}
            {spb.status !== "dibatalkan" && (
              <Button variant="outline"
                      className="rounded-lg border-destructive text-destructive hover:bg-destructive/10"
                      onClick={onCancel}
                      data-testid="cancel-spb-button">
                Batalkan SPB
              </Button>
            )}
            {(spb.status === "belum_termuat" || spb.status === "dibatalkan") ? (
              <Button variant="outline"
                      className="rounded-lg border-destructive text-destructive hover:bg-destructive/10"
                      onClick={onDelete}
                      data-testid="delete-spb-button">
                Hapus Permanen
              </Button>
            ) : (
              <div
                className="inline-flex items-center gap-2 text-xs text-muted-foreground border border-dashed border-border px-3 py-2 bg-secondary/20"
                title="SPB yang sudah termuat/mismatch tidak bisa dihapus permanen — gunakan Batalkan"
                data-testid="delete-blocked-hint"
              >
                Hapus permanen terkunci — SPB sudah termuat. Gunakan <strong>Batalkan</strong> jika perlu.
              </div>
            )}
          </div>
          {spb.status === "dibatalkan" && spb.cancel_reason && (
            <div className="mt-3 text-xs text-muted-foreground">
              <strong>Alasan pembatalan:</strong> {spb.cancel_reason}
              {spb.cancelled_by && (<span> · oleh <span className="font-mono">{spb.cancelled_by}</span></span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
